// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
// Dossier de mise en service d'un équipement (K1) — le modèle d'étapes.
//
// DEUX PARTIS PRIS, contre les deux façons de rater ce chantier :
//
// 1. Ce n'est PAS un tunnel. La mise en service d'un automate s'étale sur des
//    semaines, avec des étapes suspendues à des tiers — livraison, intervention
//    du fournisseur, raccordement au SIL. Un formulaire multi-étapes à finir en
//    une session serait inutilisable ici. C'est donc un dossier ouvert, avec une
//    check-list persistante que l'on coche au fil des semaines.
//
// 2. La séquence n'est pas imposée. Un assistant qui impose SA séquence à un
//    laboratoire qui a la sienne est contourné en une semaine. Ce qui suit est
//    un MODÈLE PAR DÉFAUT : il est copié dans le dossier à l'ouverture, et
//    chaque dossier reste modifiable — on ajoute, on retire, on renomme, on
//    change ce qui bloque. Le laboratoire peut aussi enregistrer son propre
//    modèle dans le paramétrage (`ged-setting.mesModeles`).
//
// Le rattachement d'une étape à un article ISO n'est pas décoratif : c'est ce
// qui permet de répondre « voici comment nous répondons au §7.3.1 » en montrant
// une pièce, et non une intention.

export interface EtapeMES {
  id: string;
  ordre: number;
  libelle: string;
  /** Article ISO qui justifie l'étape — alimente la pastille `<Norme>`. */
  art?: string;
  /** Ce qu'on doit pouvoir montrer à un auditeur une fois l'étape faite. */
  piece?: string;
  /** Une étape bloquante interdit la mise en service tant qu'elle n'est pas faite. */
  bloquante: boolean;
  /** Renseignés au fil du dossier. */
  responsable?: string;
  echeance?: string;
  fait?: boolean;
  faitLe?: string;
  faitPar?: string;
  note?: string;
  /** Sans objet pour cet équipement — écarté, avec son motif. */
  sansObjet?: boolean;
  motifSansObjet?: string;
}

export interface DossierMES {
  ouvertLe: string;
  ouvertPar: string;
  etapes: EtapeMES[];
  /** Décision finale : c'est elle qui autorise l'usage, pas le simple fait que
   *  toutes les cases soient cochées. */
  autoriseLe?: string;
  autorisePar?: string;
}

/** Contacts qu'on cherche à 7 h du matin quand l'automate est en panne. */
export interface ContactEquip {
  id: string;
  role: 'fournisseur' | 'sav' | 'referent' | 'autre';
  nom: string;
  organisme?: string;
  telephone?: string;
  email?: string;
  /** Délai d'intervention contractuel — l'information qu'on ne retrouve jamais. */
  delaiContractuel?: string;
}

export const ROLES_CONTACT: Record<ContactEquip['role'], string> = {
  fournisseur: 'Fournisseur',
  sav: 'SAV / technicien',
  referent: 'Référent interne',
  autre: 'Autre',
};

/**
 * Séquence par défaut, dans l'ordre d'un achat d'automate.
 *
 * `bloquante` marque ce sans quoi l'appareil ne doit produire aucun résultat
 * patient : réception, identification, qualification, vérification des
 * performances, habilitation des utilisateurs, autorisation. Les autres étapes
 * comptent, mais n'empêchent pas la mise en service.
 */
export const MES_MODELE_DEFAUT: Omit<EtapeMES, 'id'>[] = [
  { ordre: 1, libelle: 'Expression du besoin et validation', art: '6.8', piece: 'Demande d’achat', bloquante: false },
  { ordre: 2, libelle: 'Sélection du fournisseur et commande', art: '6.8', piece: 'Bon de commande', bloquante: false },
  { ordre: 3, libelle: 'Réception et vérification de conformité à la commande', art: '6.4', piece: 'PV de réception', bloquante: true },
  { ordre: 4, libelle: 'Inventaire : identification, n° de série, emplacement', art: '6.4', piece: 'Fiche équipement renseignée', bloquante: true },
  { ordre: 5, libelle: 'Raccordements (électrique, eau, informatique / SIL)', art: '6.3', piece: 'Attestation de raccordement', bloquante: false },
  { ordre: 6, libelle: 'Étalonnage ou qualification initiale', art: '6.5', piece: 'Certificat d’étalonnage', bloquante: true },
  { ordre: 7, libelle: 'Vérification des performances (justesse, fidélité)', art: '7.3.1', piece: 'Dossier de vérification', bloquante: true },
  { ordre: 8, libelle: 'Rédaction du mode opératoire', art: '7.3.4', piece: 'Document maîtrisé lié', bloquante: false },
  { ordre: 9, libelle: 'Formation et habilitation des utilisateurs', art: '6.2', piece: 'Matrice d’habilitations à jour', bloquante: true },
  { ordre: 10, libelle: 'Plan de maintenance et d’étalonnage périodiques', art: '6.4', piece: 'Échéancier', bloquante: false },
  { ordre: 11, libelle: 'Intégration au programme de contrôle interne', art: '7.3.5', piece: 'Carte de contrôle paramétrée', bloquante: false },
  { ordre: 12, libelle: 'Autorisation de mise en service', art: '6.4', piece: 'Décision signée', bloquante: true },
];

/** Une étape écartée ne compte ni comme faite ni comme due. */
export const etapeDue = (e: EtapeMES) => !e.fait && !e.sansObjet;

export function avancement(d: DossierMES | null | undefined) {
  const etapes = d?.etapes || [];
  const retenues = etapes.filter((e) => !e.sansObjet);
  const faites = retenues.filter((e) => e.fait);
  const bloquantesDues = retenues.filter((e) => e.bloquante && !e.fait);
  return {
    total: retenues.length,
    faites: faites.length,
    ecartees: etapes.length - retenues.length,
    bloquantesDues,
    /** Toutes les étapes bloquantes sont franchies : la mise en service est
     *  autorisable — la décision reste un acte, pas une conséquence. */
    autorisable: retenues.length > 0 && bloquantesDues.length === 0,
    pct: retenues.length ? Math.round((faites.length / retenues.length) * 100) : 0,
  };
}

/**
 * État réel d'un équipement, DÉRIVÉ du dossier et non saisi à la main.
 *
 * Tant que le dossier n'est pas autorisé, l'équipement est « en cours de mise
 * en service » — et il ne doit produire aucun résultat patient. Un statut
 * saisi librement se serait mis à « en service » le jour de la livraison,
 * avant toute vérification.
 */
export function etatEquip(statut: string, d: DossierMES | null | undefined): {
  cle: string; label: string; utilisable: boolean;
} {
  if (statut === 'hors_service') return { cle: 'hors_service', label: 'Hors service', utilisable: false };
  if (statut === 'maintenance') return { cle: 'maintenance', label: 'En maintenance', utilisable: false };
  if (d && !d.autoriseLe) return { cle: 'mise_en_service', label: 'En cours de mise en service', utilisable: false };
  return { cle: 'en_service', label: 'En service', utilisable: true };
}
